import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { 
  Building2, 
  Users, 
  DollarSign, 
  AlertTriangle, 
  Eye, 
  EyeOff,
  UserX, 
  CheckCircle, 
  XCircle,
  Settings,
  BarChart3,
  FileText,
  Download,
  RefreshCw,
  Search,
  Edit,
  Lock,
  Trash2,
  Plus,
  Check,
  BookOpen,
  Stethoscope,
  Mail,
  HelpCircle,
  MessageCircle,
  Shield
} from 'lucide-react';
import LoadingSpinner from './LoadingSpinner';
import { adminApi } from '../services/authApi';
import AdminSupport from './AdminSupport';
import { WelcomeVideoStatsAdmin } from './WelcomeVideoStatsAdmin';
import ComplianceAcknowledgmentsAdmin from './ComplianceAcknowledgmentsAdmin';
import { BaaAdminPanel } from './baa/BaaAdminPanel';
import { BetaProgramAdmin } from './beta-admin/BetaProgramAdmin';
import { OfficesAdmin } from './admin-offices/OfficesAdmin';
import { EnterpriseInquiriesAdmin } from './admin-offices/EnterpriseInquiriesAdmin';
import PracticeInfo from './PracticeInfo';
import TutorialManagement from './TutorialManagement';
import BetaTesterManagement from './BetaTesterManagement';
import DisclaimerAcceptancesView from './DisclaimerAcceptancesView';
import AdminAnnouncementsPanel from './AdminAnnouncementsPanel';
import MedCardDisclaimerView from './MedCardDisclaimerView';
import EmailActivityMonitor from './EmailActivityMonitor';
import SmileDesignLinks from './SmileDesignLinks';
import DentalStaffAdminPanel from './DentalStaffAdminPanel';
import ReinterviewPanel from './ReinterviewPanel';
import TestimonialsAdminPanel from './TestimonialsAdminPanel';
import LeadMagnetAdminPanel from './LeadMagnetAdminPanel';
import EcardRequestsAdminPanel from './EcardRequestsAdminPanel';
import AdminBillingPanel from './AdminBillingPanel';
import SanitizeContentCard from './SanitizeContentCard';
import CheckInCampaignCard from './CheckInCampaignCard';
import BroadcastEmailCard from './BroadcastEmailCard';
import EmailAuditCard from './EmailAuditCard';
import AppSettingsCard from './AppSettingsCard';
import StandaloneMigrationCard from './StandaloneMigrationCard';
import ABTestCard from './ABTestCard';
import HealthCheckCard from './HealthCheckCard';
import AdminAnalytics from './AdminAnalytics';
import AdminAppHealth from './AdminAppHealth';
import AdminShell from './AdminShell';
import AdminDashboardKPIs from './AdminDashboardKPIs';
import AdminSmsLogsPanel from './AdminSmsLogsPanel';

// Error Boundary to catch React errors
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error: error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Error Boundary caught error:', error);
    console.error('Error Info:', errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
          <h3 className="font-bold">React Error Caught:</h3>
          <p>Error: {this.state.error?.message || 'Unknown error'}</p>
          <button 
            type="button"
            onClick={() => this.setState({ hasError: false, error: null })}
            className="mt-2 bg-red-500 text-white px-3 py-1 rounded text-sm"
          >
            Try Again
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

// Simple custom Tabs component to avoid Radix UI issues
const CustomTabs = ({ children, value, onValueChange, className }) => {
  return <div className={className || ''}>{children}</div>;
};

const CustomTabsList = ({ children, className }) => {
  return (
    <div className={`flex border-b border-gray-200 ${className || ''}`}>
      {children}
    </div>
  );
};

const CustomTabsTrigger = ({ children, value, activeTab, onTabChange, className }) => {
  const isActive = value === activeTab;
  return (
    <button
      type="button"
      className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 ${
        isActive 
          ? 'border-red-500 text-red-600 bg-red-50' 
          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
      } ${className || ''}`}
      onClick={() => onTabChange && onTabChange(value)}
    >
      {children}
    </button>
  );
};

const CustomTabsContent = ({ children, value, activeTab, className }) => {
  if (value !== activeTab) return null;
  return <div className={className || ''}>{children}</div>;
};

const AdminDashboard = ({ adminToken, setAdminToken }) => {
  const [loading, setLoading] = useState(false);
  const [errorState, setErrorState] = useState('');
  // adminToken is now passed as a prop
  
  // Safe error setter that ensures error is always a string
  const setError = (errorValue) => {
    console.log('setError called with:', errorValue, 'Type:', typeof errorValue);
    const safeError = typeof errorValue === 'string' ? errorValue : 
                     typeof errorValue === 'object' && errorValue !== null ? JSON.stringify(errorValue) : 
                     String(errorValue || '');
    console.log('Setting error state to safe error:', safeError); // Debug log
    setErrorState(safeError);
  };
  
  const error = errorState;
  const [dashboardData, setDashboardData] = useState(null);
  const [practices, setPractices] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedPractice, setSelectedPractice] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // all, active, inactive, trial
  const [activeTab, setActiveTab] = useState('dashboard');
  const [showTutorialManagement, setShowTutorialManagement] = useState(false);
  const [showBetaTesterManagement, setShowBetaTesterManagement] = useState(false);
  const [showDisclaimerAcceptances, setShowDisclaimerAcceptances] = useState(false);
  const [showMedCardDisclaimers, setShowMedCardDisclaimers] = useState(false);
  const [showEmailActivityMonitor, setShowEmailActivityMonitor] = useState(false);
  
  // Procedures state
  const [procedures, setProcedures] = useState([]);
  const [specialties, setSpecialties] = useState([]);
  const [editingProcedure, setEditingProcedure] = useState(null);
  const [showAddProcedureForm, setShowAddProcedureForm] = useState(false);
  const [showAddPracticeForm, setShowAddPracticeForm] = useState(false);
  const [showSelectPracticeModal, setShowSelectPracticeModal] = useState(false);
  const [selectedPracticeForDeployment, setSelectedPracticeForDeployment] = useState('');
  const [practiceSearchTerm, setPracticeSearchTerm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showTempPassword, setShowTempPassword] = useState(false);
  const [newPractice, setNewPractice] = useState({
    practiceName: '',
    adminEmail: '',
    adminFirstName: '',
    adminLastName: '',
    phone: '',
    address: '',
    tempPassword: '',
    subscriptionType: 'trial',
    trialDays: 30
  });
  const [newProcedure, setNewProcedure] = useState({
    name: '',
    specialty: '',
    specialtyName: '',
    duration: '',
    purpose: '',
    first24Hours: [''],
    painAndSwelling: [''],
    oralHygiene: [''],
    diet: [''],
    activity: [''],
    specialPrecautions: [''],
    followUp: ['']
  });

  // Registrations state
  const [registrations, setRegistrations] = useState([]);
  const [registrationsLoading, setRegistrationsLoading] = useState(false);
  const [refreshingPractices, setRefreshingPractices] = useState(false);

  console.log('VERSION 2.0 LOADED - Using dental-admin-3 backend');
  const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api/admin`;
  
  // Generate a secure random password
  const generateSecurePassword = () => {
    const length = 12;
    const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const lowercase = 'abcdefghijklmnopqrstuvwxyz';
    const numbers = '0123456789';
    const symbols = '!@#$%^&*';
    const allChars = uppercase + lowercase + numbers + symbols;
    
    let password = '';
    // Ensure at least one character from each category
    password += uppercase[Math.floor(Math.random() * uppercase.length)];
    password += lowercase[Math.floor(Math.random() * lowercase.length)];
    password += numbers[Math.floor(Math.random() * numbers.length)];
    password += symbols[Math.floor(Math.random() * symbols.length)];
    
    // Fill the rest randomly
    for (let i = 4; i < length; i++) {
      password += allChars[Math.floor(Math.random() * allChars.length)];
    }
    
    // Shuffle the password
    return password.split('').sort(() => Math.random() - 0.5).join('');
  };

  // Helper function to handle API error responses
  const handleApiError = (errorData, fallbackMessage) => {
    console.log('handleApiError called with errorData:', errorData);
    try {
      // Handle FastAPI validation errors
      if (errorData && errorData.detail) {
        if (Array.isArray(errorData.detail)) {
          console.log('Processing array of validation errors');
          const errorMessages = errorData.detail.map(err => {
            if (typeof err === 'object' && err.msg) {
              const location = err.loc ? err.loc.join(' -> ') + ': ' : '';
              return `${location}${err.msg}`;
            }
            return String(err);
          });
          const result = errorMessages.join(', ');
          console.log('Array processing result:', result);
          return result;
        } else if (typeof errorData.detail === 'string') {
          console.log('String detail found:', errorData.detail);
          return errorData.detail;
        } else if (typeof errorData.detail === 'object' && errorData.detail.msg) {
          console.log('Object detail with msg found:', errorData.detail.msg);
          return errorData.detail.msg;
        }
      }
      
      if (typeof errorData === 'string') {
        console.log('String errorData found:', errorData);
        return errorData;
      }
      
      console.log('Using fallback message:', fallbackMessage);
      return fallbackMessage;
    } catch (e) {
      console.error('Error in handleApiError:', e);
      return fallbackMessage;
    }
  };

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    if (!adminToken) {
      console.error('🚨 loadDashboardData called without adminToken');
      return;
    }
    
    console.log('📊 Loading dashboard data with token:', adminToken.substring(0, 20) + '...');
    setLoading(true);
    
    // Use XMLHttpRequest to avoid extension interference
    const xhr = new XMLHttpRequest();
    xhr.open('GET', `${API_BASE}/dashboard`, true);
    xhr.setRequestHeader('Authorization', `Bearer ${getToken()}`);
    
    xhr.onreadystatechange = function() {
      if (xhr.readyState === 4) {
        setLoading(false);
        
        if (xhr.status === 200) {
          try {
            const data = JSON.parse(xhr.responseText);
            console.log('✅ Dashboard data loaded:', data);
            setDashboardData(data);
          } catch (e) {
            console.error('❌ Failed to parse dashboard data:', e);
            setError('Failed to parse dashboard data');
          }
        } else if (xhr.status === 401) {
          console.warn('🚨 Token expired, clearing...');
          localStorage.removeItem('adminToken');
          setAdminToken(null);
          setError('Admin session expired. Please login again.');
        } else {
          console.error('❌ Dashboard load failed:', xhr.status);
          setError('Failed to load admin dashboard');
        }
      }
    };
    
    xhr.onerror = function() {
      setLoading(false);
      console.error('❌ Network error loading dashboard');
      setError('Network error while loading dashboard');
    };
    
    xhr.send();
  };

  // Helper to get the freshest admin token
  const getToken = () => adminToken || localStorage.getItem('adminToken');

  const loadPractices = async () => {
    setRefreshingPractices(true);
    console.log('🔄 Refreshing practices list...');
    const currentToken = getToken();
    if (!currentToken) { setRefreshingPractices(false); return; }
    
    const xhr = new XMLHttpRequest();
    xhr.open('GET', `${API_BASE}/practices?limit=100&t=${Date.now()}`, true);
    xhr.setRequestHeader('Authorization', `Bearer ${currentToken}`);
    xhr.setRequestHeader('Cache-Control', 'no-cache');
    
    xhr.onreadystatechange = function() {
      if (xhr.readyState === 4) {
        setRefreshingPractices(false);
        if (xhr.status === 200) {
          try {
            const data = JSON.parse(xhr.responseText);
            console.log('🔄 ADMIN PRACTICES LOADED:', {
              total: data.practices?.length || 0,
              hasCaryganz: data.practices?.some(p => p.email === 'caryganz@gmail.com'),
              hasCaryConsulting: data.practices?.some(p => p.email === 'caryganzconsulting@gmail.com')
            });
            setPractices(data.practices || []);
            console.log(`✅ Loaded ${data.practices?.length || 0} practices including SamCart customers`);
          } catch (e) {
            console.error('Failed to parse practices:', e);
          }
        }
      }
    };
    
    xhr.onerror = function() {
      setRefreshingPractices(false);
      console.error('Failed to load practices');
      alert('Failed to refresh practices. Please try again.');
    };
    
    xhr.send();
  };

  const loadUsers = async (practiceId) => {
    const xhr = new XMLHttpRequest();
    xhr.open('GET', `${API_BASE}/users/${practiceId}`, true);
    xhr.setRequestHeader('Authorization', `Bearer ${getToken()}`);
    
    xhr.onreadystatechange = function() {
      if (xhr.readyState === 4 && xhr.status === 200) {
        try {
          const data = JSON.parse(xhr.responseText);
          setUsers(data.users || []);
        } catch (e) {
          console.error('Failed to parse users:', e);
        }
      }
    };
    
    xhr.send();
  };

  const loadRegistrations = async () => {
    try {
      setRegistrationsLoading(true);
      const response = await fetch(`${API_BASE}/registration-attempts`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });

      if (response.ok) {
        const data = await response.json();
        setRegistrations(data.data || []);
      } else {
        console.error('Failed to load registrations');
      }
    } catch (error) {
      console.error('Failed to load registrations:', error);
    } finally {
      setRegistrationsLoading(false);
    }
  };

  const deleteRegistrationAttempt = async (attemptId, email) => {
    if (!confirm(`Are you sure you want to delete the registration attempt for ${email}?`)) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/registration-attempts/${attemptId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${getToken()}`
        }
      });

      if (response.ok) {
        alert('Registration attempt deleted successfully');
        loadRegistrations(); // Reload the list
      } else {
        const errorData = await response.json();
        alert(`Failed to delete: ${errorData.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Delete error:', error);
      alert('Failed to delete registration attempt');
    }
  };

  const createPractice = async () => {
    try {
      // Validate required fields
      if (!newPractice.practiceName.trim()) {
        alert('Practice name is required');
        return;
      }
      if (!newPractice.adminEmail.trim()) {
        alert('Admin email is required');
        return;
      }
      if (!newPractice.adminFirstName.trim()) {
        alert('Admin first name is required');
        return;
      }
      if (!newPractice.adminLastName.trim()) {
        alert('Admin last name is required');
        return;
      }
      if (!newPractice.tempPassword || newPractice.tempPassword.length < 8) {
        alert('Temporary password must be at least 8 characters long');
        return;
      }

      setLoading(true);
      const response = await fetch(`${API_BASE}/create-practice`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getToken()}`
        },
        body: JSON.stringify(newPractice)
      });
      
      const data = await response.json();
      
      if (data.success) {
        const practiceData = {
          practiceName: newPractice.practiceName
        };
        
        const adminCredentials = {
          adminEmail: newPractice.adminEmail,
          adminFirstName: newPractice.adminFirstName,
          adminLastName: newPractice.adminLastName,
          tempPassword: newPractice.tempPassword
        };
        
        // Show success message with option to send email
        console.log('🔍 About to show email confirmation dialog');
        const sendEmail = confirm(`Practice "${newPractice.practiceName}" created successfully!\n\n📧 Send welcome email to customer now?\n\nThis will send login credentials to: ${newPractice.adminEmail}`);
        console.log('🔍 Email dialog result:', sendEmail);
        
        if (sendEmail) {
          console.log('🔍 User chose to send email, making API call...');
          try {
            console.log('🔍 Making email API call to:', `${API_BASE}/send-welcome-email`);
            console.log('🔍 Email payload:', { practiceData, adminCredentials, appUrl: process.env.REACT_APP_BACKEND_URL });
            const emailResponse = await fetch(`${API_BASE}/send-welcome-email`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${getToken()}`
              },
              body: JSON.stringify({
                practiceData: practiceData,
                adminCredentials: adminCredentials,
                appUrl: process.env.REACT_APP_BACKEND_URL
              })
            });
            
            console.log('🔍 Email API response status:', emailResponse.status);
            const emailData = await emailResponse.json();
            console.log('🔍 Email API response data:', emailData);
            
            if (emailData.success) {
              alert(`✅ Welcome email sent successfully to ${newPractice.adminEmail}!\n\nThe customer has been notified with their login credentials.`);
            } else {
              alert(`⚠️ Practice created but email failed to send.\n\nPlease manually send these credentials:\nEmail: ${newPractice.adminEmail}\nPassword: ${newPractice.tempPassword}`);
            }
          } catch (emailError) {
            console.error('🔍 Email sending failed:', emailError);
            console.error('🔍 Email error details:', emailError.message, emailError.stack);
            alert(`⚠️ Practice created but email failed to send.\n\nPlease manually send these credentials:\nEmail: ${newPractice.adminEmail}\nPassword: ${newPractice.tempPassword}`);
          }
        } else {
          console.log('🔍 User chose NOT to send email');
          // Show credentials for manual sharing
          alert(`Practice created! Manual credentials:\n\nEmail: ${newPractice.adminEmail}\nPassword: ${newPractice.tempPassword}\n\n⚠️ Remember to send these to the customer.`);
        }
        
        // Reset form
        setNewPractice({
          practiceName: '',
          adminEmail: '',
          adminFirstName: '',
          adminLastName: '',
          phone: '',
          address: '',
          tempPassword: '',
          subscriptionType: 'trial',
          trialDays: 30
        });
        setShowAddPracticeForm(false);
        loadPractices(); // Refresh the practices list
      } else {
        alert(`Failed to create practice: ${data.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Create practice error:', error);
      alert(`Error creating practice: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const managePractice = async (practiceId, action, reason = '') => {
    try {
      const response = await fetch(`${API_BASE}/manage-practice`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getToken()}`
        },
        body: JSON.stringify({
          practice_id: practiceId,
          action: action,
          reason: reason
        })
      });

      if (response.ok) {
        loadPractices();
        loadDashboardData();
      }
    } catch (error) {
      console.error('Failed to manage practice:', error);
    }
  };

  const updateSubscriptionStatus = async (practiceId, practiceName, currentStatus) => {
    try {
      const statusOptions = ['active', 'trial', 'cancelled', 'payment_failed', 'beta'];
      const statusMessage = `Update subscription status for "${practiceName}"?\n\nCurrent Status: ${currentStatus}\n\nSelect new status:\n1 - active\n2 - trial\n3 - cancelled\n4 - payment_failed\n5 - beta`;
      
      const choice = window.prompt(statusMessage, '1');
      if (!choice) return;
      
      const statusIndex = parseInt(choice) - 1;
      if (statusIndex < 0 || statusIndex >= statusOptions.length) {
        alert('Invalid choice. Please enter 1-5');
        return;
      }
      
      const newStatus = statusOptions[statusIndex];
      const reason = window.prompt(`Reason for changing to "${newStatus}"? (optional)`, '');
      
      setLoading(true);
      
      const response = await fetch(`${API_BASE}/update-subscription-status`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          practice_id: practiceId,
          status: newStatus,
          reason: reason || undefined
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        alert(`✅ Subscription status updated to: ${newStatus}\n\nPractice: ${practiceName}\nActive: ${data.is_active ? 'Yes' : 'No'}`);
        loadPractices(); // Refresh
        loadDashboardData();
      } else {
        alert(`Failed to update status: ${data.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Update subscription status error:', error);
      alert(`Error updating status: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const resendWelcomeEmail = async (practice) => {
    try {
      const confirm = window.confirm(
        `📧 Resend welcome email to "${practice.name}"?\n\nThis will send login credentials to: ${practice.email}\n\nNote: Check spam folder if email is not received.`
      );
      
      if (!confirm) return;
      
      setLoading(true);
      
      const response = await fetch(`${API_BASE}/send-welcome-email`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          practiceData: {
            practiceName: practice.name
          },
          adminCredentials: {
            adminEmail: practice.email,
            adminFirstName: practice.adminFirstName || 'Admin',
            adminLastName: practice.adminLastName || 'User',
            tempPassword: practice.tempPassword || 'Please contact support for password'
          },
          appUrl: process.env.REACT_APP_BACKEND_URL
        })
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        alert(`✅ Welcome email resent successfully to ${practice.email}!\n\nPlease check inbox and spam folder.`);
      } else {
        alert(`❌ Failed to resend email: ${data.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Resend email error:', error);
      alert('❌ Network error while resending email');
    } finally {
      setLoading(false);
    }
  };
  const deletePractice = async (practiceId, practiceName) => {
    if (!adminToken) return;
    
    const confirmed = window.confirm(
      `⚠️ WARNING: Are you sure you want to PERMANENTLY DELETE the practice "${practiceName}"?\n\nThis will delete:\n- The practice record\n- All users (dentists/staff)\n- All patients\n- All procedure assignments\n- All practice data\n\nThis action CANNOT be undone!`
    );
    
    if (!confirmed) return;
    
    // Double confirmation for safety
    const doubleConfirmed = window.confirm(
      `FINAL CONFIRMATION: Type "${practiceName}" to confirm deletion.\n\nAre you absolutely sure you want to delete "${practiceName}" and ALL its data permanently?`
    );
    
    if (!doubleConfirmed) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/practices/${practiceId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${getToken()}`
        }
      });

      if (response.ok) {
        await loadPractices();
        await loadDashboardData();
        alert(`Practice "${practiceName}" has been permanently deleted.`);
        
        // Clear selected practice if it was the deleted one
        if (selectedPractice?.id === practiceId) {
          setSelectedPractice(null);
          setUsers([]);
        }
      } else {
        const errorData = await response.json();
        setError(handleApiError(errorData, 'Failed to delete practice'));
      }
    } catch (error) {
      console.error('Failed to delete practice:', error);
      setError('Failed to delete practice');
    }
    setLoading(false);
  };

  const resetUserPassword = async (userId, newPassword) => {
    try {
      const response = await fetch(`${API_BASE}/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getToken()}`
        },
        body: JSON.stringify({
          user_id: userId,
          new_password: newPassword
        })
      });

      return response.ok;
    } catch (error) {
      console.error('Failed to reset password:', error);
      return false;
    }
  };

  const deleteUser = async (userId, userName) => {
    if (!adminToken) return;
    
    const confirmed = window.confirm(
      `Are you sure you want to delete user "${userName}"? This action cannot be undone.`
    );
    
    if (!confirmed) return;
    
    try {
      const response = await fetch(`${API_BASE}/users/${userId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${getToken()}`
        }
      });

      if (response.ok) {
        // Reload users for the current practice
        if (selectedPractice) {
          await loadUsers(selectedPractice.id);
        }
        alert(`User "${userName}" has been deleted successfully.`);
      } else {
        const errorData = await response.json();
        setError(handleApiError(errorData, 'Failed to delete user'));
      }
    } catch (error) {
      console.error('Failed to delete user:', error);
      setError('Failed to delete user');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    window.location.reload();
  };

  // Load practices when switching to practices tab
  useEffect(() => {
    if (activeTab === 'practices') {
      loadPractices();
    } else if (activeTab === 'procedures') {
      loadProcedures();
    } else if (activeTab === 'users') {
      // Load practices for the users tab so user can select a practice
      loadPractices();
    } else if (activeTab === 'registrations') {
      loadRegistrations();
    }
  }, [activeTab]);

  // Procedures management functions
  const loadProcedures = async () => {
    if (!adminToken) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/procedures`, {
        headers: {
          'Authorization': `Bearer ${getToken()}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setProcedures(data.procedures || []);
        setSpecialties(data.specialties || []);
      }
    } catch (error) {
      console.error('Failed to load procedures:', error);
      setError('Failed to load procedures');
    }
    setLoading(false);
  };

  // Create a separate handler function as recommended by troubleshoot agent
  const handleCreateProcedure = async () => {
    console.log('🚀 HANDLE CREATE PROCEDURE CALLED - React 19 Fix');
    try {
      await createProcedure();
    } catch (error) {
      console.error('❌ Error in handleCreateProcedure:', error);
      setError(`Unexpected error: ${error.message || error}`);
    }
  };

  const createProcedure = async () => {
    if (!adminToken) return;
    
    setLoading(true);
    setError(''); // Clear any previous errors
    
    try {
      const response = await fetch(`${API_BASE}/procedures`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getToken()}`
        },
        body: JSON.stringify({
          ...newProcedure,
          first24Hours: newProcedure.first24Hours.filter(item => item.trim()),
          painAndSwelling: newProcedure.painAndSwelling.filter(item => item.trim()),
          oralHygiene: newProcedure.oralHygiene.filter(item => item.trim()),
          diet: newProcedure.diet.filter(item => item.trim()),
          activity: newProcedure.activity.filter(item => item.trim()),
          specialPrecautions: newProcedure.specialPrecautions.filter(item => item.trim()),
          followUp: newProcedure.followUp.filter(item => item.trim())
        })
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Procedure created successfully:', data);
        
        // Reset form and close
        setShowAddProcedureForm(false);
        setNewProcedure({
          name: '',
          specialty: '',
          specialtyName: '',
          duration: '',
          purpose: '',
          first24Hours: [''],
          painAndSwelling: [''],
          oralHygiene: [''],
          diet: [''],
          activity: [''],
          specialPrecautions: [''],
          followUp: ['']
        });
        
        // Refresh procedures list
        await loadProcedures();
        
      } else {
        // Handle error responses - FIX: Convert objects to strings
        console.log('API Error - Response status:', response.status);
        try {
          const errorData = await response.json();
          console.log('Raw error data:', errorData);
          
          // CRITICAL FIX: Always convert error objects to strings
          let errorMessage;
          if (Array.isArray(errorData.detail)) {
            // Handle FastAPI validation errors
            const errors = errorData.detail.map(err => `${err.loc?.join('.')||'field'}: ${err.msg}`);
            errorMessage = `Validation errors: ${errors.join(', ')}`;
          } else if (typeof errorData.detail === 'string') {
            errorMessage = errorData.detail;
          } else if (errorData.message) {
            errorMessage = errorData.message;
          } else {
            errorMessage = `Server error (${response.status}): Please check all required fields`;
          }
          
          console.log('Processed error message:', errorMessage);
          setError(errorMessage);
        } catch (parseError) {
          console.error('Failed to parse error response:', parseError);
          const errorMessage = `Server error (${response.status}): Failed to create procedure`;
          setError(errorMessage);
        }
      }
    } catch (networkError) {
      console.error('Network error during procedure creation:', networkError);
      const errorMessage = `Network error: ${networkError.message || 'Failed to create procedure'}`;
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const updateProcedure = async (procedureId, updates) => {
    if (!adminToken) return;
    
    try {
      const response = await fetch(`${API_BASE}/procedures/${procedureId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getToken()}`
        },
        body: JSON.stringify(updates)
      });

      if (response.ok) {
        await loadProcedures();
        setEditingProcedure(null);
      } else {
        const errorData = await response.json();
        setError(handleApiError(errorData, 'Failed to update procedure'));
      }
    } catch (error) {
      console.error('Failed to update procedure:', error);
      setError('Failed to update procedure');
    }
  };

  const deleteProcedure = async (procedureId) => {
    if (!adminToken) return;
    if (!window.confirm('Are you sure you want to delete this procedure?')) return;
    
    try {
      const response = await fetch(`${API_BASE}/procedures/${procedureId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${getToken()}`
        }
      });

      if (response.ok) {
        await loadProcedures();
      } else {
        const errorData = await response.json();
        setError(handleApiError(errorData, 'Failed to delete procedure'));
      }
    } catch (error) {
      console.error('Failed to delete procedure:', error);
      setError('Failed to delete procedure');
    }
  };

  const deployProcedures = async (procedureIds, deploymentType, specificPracticeId = null) => {
    if (!adminToken) return;
    
    try {
      const payload = {
        procedure_ids: procedureIds,
        deployment_type: deploymentType
      };
      
      if (specificPracticeId) {
        payload.specific_practice_id = specificPracticeId;
      }
      
      const response = await fetch(`${API_BASE}/procedures/deploy`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getToken()}`
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const data = await response.json();
        alert(`Successfully deployed ${procedureIds.length} procedures to ${data.deployment_result.deployed_to_practices} practices`);
      } else {
        const errorData = await response.json();
        setError(handleApiError(errorData, 'Failed to deploy procedures'));
      }
    } catch (error) {
      console.error('Failed to deploy procedures:', error);
      setError('Failed to deploy procedures');
    }
  };

  const addArrayItem = (field, index = null) => {
    setNewProcedure(prev => ({
      ...prev,
      [field]: [...prev[field], '']
    }));
  };

  const updateArrayItem = (field, index, value) => {
    setNewProcedure(prev => ({
      ...prev,
      [field]: prev[field].map((item, i) => i === index ? value : item)
    }));
  };

  const removeArrayItem = (field, index) => {
    setNewProcedure(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  if (loading && !dashboardData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-red-50 flex items-center justify-center">
        <LoadingSpinner size="xl" />
      </div>
    );
  }

  return (
    <AdminShell
      activeTab={activeTab}
      onTabChange={setActiveTab}
      onLogout={handleLogout}
      onToolClick={{
        betaTesters:        () => setShowBetaTesterManagement(true),
        icDisclaimers:      () => setShowDisclaimerAcceptances(true),
        medCardDisclaimers: () => setShowMedCardDisclaimers(true),
        tutorials:          () => setShowTutorialManagement(true),
        emailActivity:      () => setShowEmailActivityMonitor(true),
        resetDisclaimer:    () => window.open('/admin/reset-disclaimer', '_blank'),
      }}
    >
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
          {typeof error === 'string' ? error : JSON.stringify(error)}
        </div>
      )}

      <CustomTabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <CustomTabsContent value="dashboard" activeTab={activeTab} className="space-y-6">
          <AdminDashboardKPIs dashboardData={dashboardData} />
          </CustomTabsContent>

          <CustomTabsContent value="practices" activeTab={activeTab} className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold">Practice Management</h2>
              <div className="flex space-x-2">
                <Button onClick={loadPractices} disabled={refreshingPractices}>
                  <RefreshCw className={`h-4 w-4 mr-2 ${refreshingPractices ? 'animate-spin' : ''}`} />
                  {refreshingPractices ? 'Refreshing...' : 'Refresh'}
                </Button>
                <Button onClick={() => {
                  const newPassword = generateSecurePassword();
                  setNewPractice(prev => ({...prev, tempPassword: newPassword}));
                  setShowAddPracticeForm(true);
                }}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Practice
                </Button>
              </div>
            </div>

            {/* Add Practice Form */}
            {showAddPracticeForm && (
              <Card>
                <CardHeader>
                  <CardTitle>Add New Dental Practice</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        Practice Name <span className="text-red-500">*</span>
                      </label>
                      <Input 
                        value={newPractice.practiceName}
                        onChange={(e) => setNewPractice(prev => ({...prev, practiceName: e.target.value}))}
                        placeholder="e.g., Smith Family Dental"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        Admin Email <span className="text-red-500">*</span>
                      </label>
                      <Input 
                        type="email"
                        value={newPractice.adminEmail}
                        onChange={(e) => setNewPractice(prev => ({...prev, adminEmail: e.target.value}))}
                        placeholder="admin@example.com"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        Admin First Name <span className="text-red-500">*</span>
                      </label>
                      <Input 
                        value={newPractice.adminFirstName}
                        onChange={(e) => setNewPractice(prev => ({...prev, adminFirstName: e.target.value}))}
                        placeholder="John"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        Admin Last Name <span className="text-red-500">*</span>
                      </label>
                      <Input 
                        value={newPractice.adminLastName}
                        onChange={(e) => setNewPractice(prev => ({...prev, adminLastName: e.target.value}))}
                        placeholder="Smith"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        Phone (Optional)
                      </label>
                      <Input 
                        value={newPractice.phone}
                        onChange={(e) => setNewPractice(prev => ({...prev, phone: e.target.value}))}
                        placeholder="(555) 123-4567"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        Temporary Password <span className="text-red-500">*</span>
                      </label>
                      <div className="flex space-x-2">
                        <div className="relative flex-1">
                          <Input 
                            type={showTempPassword ? "text" : "password"}
                            value={newPractice.tempPassword}
                            onChange={(e) => setNewPractice(prev => ({...prev, tempPassword: e.target.value}))}
                            placeholder="Auto-generated or enter custom"
                            className="pr-10"
                          />
                          <button
                            type="button"
                            onClick={() => setShowTempPassword(!showTempPassword)}
                            className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-700"
                          >
                            {showTempPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </button>
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => setNewPractice(prev => ({...prev, tempPassword: generateSecurePassword()}))}
                          className="whitespace-nowrap"
                        >
                          Generate
                        </Button>
                      </div>
                      <div className="mt-1 text-xs text-gray-600">
                        <p className="font-medium">Password requirements:</p>
                        <ul className="list-disc list-inside mt-1 space-y-1">
                          <li>At least 8 characters (recommended: 12+)</li>
                          <li>Must include uppercase letter</li>
                          <li>Must include lowercase letter</li>
                          <li>Must include number</li>
                          <li>Must include special character (!@#$%^&*)</li>
                        </ul>
                        <p className="mt-2 text-blue-600 text-xs">
                          💡 Click "Generate" for a secure password that meets all requirements
                        </p>
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Address (Optional)
                    </label>
                    <Input 
                      value={newPractice.address}
                      onChange={(e) => setNewPractice(prev => ({...prev, address: e.target.value}))}
                      placeholder="123 Main St, City, State 12345"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        Subscription Type
                      </label>
                      <select
                        className="w-full p-2 border rounded-md"
                        value={newPractice.subscriptionType}
                        onChange={(e) => setNewPractice(prev => ({...prev, subscriptionType: e.target.value}))}
                      >
                        <option value="trial">Trial</option>
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                      </select>
                    </div>
                    {newPractice.subscriptionType === 'trial' && (
                      <div>
                        <label className="block text-sm font-medium mb-1">
                          Trial Days
                        </label>
                        <Input 
                          type="number"
                          value={newPractice.trialDays}
                          onChange={(e) => setNewPractice(prev => ({...prev, trialDays: parseInt(e.target.value) || 30}))}
                          min="1"
                          max="365"
                        />
                      </div>
                    )}
                  </div>

                  <div className="flex justify-end space-x-2">
                    <Button 
                      variant="outline" 
                      onClick={() => setShowAddPracticeForm(false)}
                    >
                      Cancel
                    </Button>
                    <Button 
                      onClick={createPractice} 
                      disabled={loading}
                    >
                      {loading ? <LoadingSpinner className="h-4 w-4 mr-2" /> : null}
                      Create Practice
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Search className="h-4 w-4" />
                    <Input
                      placeholder="Search practices..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="max-w-sm"
                    />
                  </div>
                  
                  {/* Status Filter Buttons */}
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-600 mr-2">Filter by status:</span>
                    <Button
                      size="sm"
                      variant={statusFilter === 'all' ? 'default' : 'outline'}
                      onClick={() => setStatusFilter('all')}
                    >
                      All
                    </Button>
                    <Button
                      size="sm"
                      variant={statusFilter === 'active' ? 'default' : 'outline'}
                      onClick={() => setStatusFilter('active')}
                    >
                      Active
                    </Button>
                    <Button
                      size="sm"
                      variant={statusFilter === 'trial' ? 'default' : 'outline'}
                      onClick={() => setStatusFilter('trial')}
                    >
                      Trial
                    </Button>
                    <Button
                      size="sm"
                      variant={statusFilter === 'beta' ? 'default' : 'outline'}
                      onClick={() => setStatusFilter('beta')}
                      className={statusFilter === 'beta' ? 'bg-purple-600 hover:bg-purple-700' : ''}
                    >
                      Beta
                    </Button>
                    <Button
                      size="sm"
                      variant={statusFilter === 'inactive' ? 'default' : 'outline'}
                      onClick={() => setStatusFilter('inactive')}
                    >
                      Inactive
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {practices
                    .filter(practice => {
                      // Text search filter
                      const matchesSearch = practice.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                           practice.email?.toLowerCase().includes(searchTerm.toLowerCase());
                      
                      // Status filter
                      if (statusFilter === 'all') return matchesSearch;
                      
                      const practiceStatus = practice.subscription?.status || 'inactive';
                      return matchesSearch && practiceStatus === statusFilter;
                    })
                    .map((practice) => (
                    <div key={practice.id} className="flex items-center justify-between p-4 border rounded-lg">
                      <div>
                        <h3 className="font-semibold">{practice.name}</h3>
                        <p className="text-sm text-gray-600">{practice.email}</p>
                        <p className="text-sm text-gray-500">
                          Created: {new Date(practice.createdAt).toLocaleDateString()}
                        </p>
                        {practice.trialExpiryEmailSent && (
                          <p className="text-xs text-green-600 flex items-center gap-1 mt-1">
                            <Mail className="h-3 w-3" />
                            Re-engagement email sent: {practice.trialExpiryEmailSentAt ? new Date(practice.trialExpiryEmailSentAt).toLocaleDateString() : 'Yes'}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center space-x-2">
                        <Badge 
                          variant={practice.subscription?.status === 'active' ? 'default' : 
                                   practice.subscription?.status === 'trial' ? 'secondary' : 
                                   practice.subscription?.status === 'beta' ? 'default' : 'destructive'}
                          className={practice.subscription?.status === 'beta' ? 'bg-purple-600' : ''}
                        >
                          {practice.subscription?.status || 'unknown'}
                        </Badge>
                        {practice.is_beta_tester && (
                          <Badge variant="outline" className="bg-purple-100 text-purple-800 border-purple-300">
                            🧪 Beta Tester
                          </Badge>
                        )}
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => {
                            setSelectedPractice(practice);
                            loadUsers(practice.id);
                            setActiveTab('users');
                          }}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => updateSubscriptionStatus(practice.id, practice.name, practice.subscription?.status)}
                          title="Update subscription status"
                        >
                          <span className="text-xs font-bold">STATUS</span>
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => managePractice(practice.id, 
                            practice.subscription?.status === 'active' ? 'deactivate' : 'activate'
                          )}
                        >
                          {practice.subscription?.status === 'active' ? (
                            <UserX className="h-4 w-4" />
                          ) : (
                            <CheckCircle className="h-4 w-4" />
                          )}
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => resendWelcomeEmail(practice)}
                          title="Resend welcome email with login credentials"
                        >
                          <Mail className="h-4 w-4" />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="destructive"
                          onClick={() => deletePractice(practice.id, practice.name)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </CustomTabsContent>

          <CustomTabsContent value="smile-links" activeTab={activeTab} className="space-y-6">
            <SmileDesignLinks adminToken={adminToken} />
          </CustomTabsContent>

          <CustomTabsContent value="practice-info" activeTab={activeTab} className="space-y-6">
            <PracticeInfo adminToken={adminToken} />
          </CustomTabsContent>

          <CustomTabsContent value="users" activeTab={activeTab} className="space-y-6">
            {selectedPractice ? (
              <Card>
                <CardHeader>
                  <CardTitle>Users for {selectedPractice.name}</CardTitle>
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      setSelectedPractice(null);
                      setUsers([]);
                    }}
                  >
                    Back to Practices List
                  </Button>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {users.map((user) => (
                      <div key={user.id} className="flex items-center justify-between p-4 border rounded-lg">
                        <div>
                          <h3 className="font-semibold">{user.firstName} {user.lastName}</h3>
                          <p className="text-sm text-gray-600">{user.email}</p>
                          <div className="flex items-center space-x-2 mt-1">
                            <Badge variant="outline">{user.role}</Badge>
                            {user.specialties && user.specialties.length > 0 && (
                              <Badge variant="secondary">{user.specialties.join(', ')}</Badge>
                            )}
                          </div>
                          <p className="text-xs text-gray-500 mt-1">
                            Created: {new Date(user.createdAt).toLocaleDateString()}
                          </p>
                        </div>
                        <div className="flex space-x-2">
                          <Button 
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              const newPassword = prompt('Enter new password:');
                              if (newPassword) {
                                resetUserPassword(user.id, newPassword);
                              }
                            }}
                          >
                            <Lock className="h-4 w-4 mr-2" />
                            Reset Password
                          </Button>
                          <Button 
                            size="sm"
                            variant="destructive"
                            onClick={() => deleteUser(user.id, user.firstName + ' ' + user.lastName)}
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete User
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {users.length === 0 && (
                    <div className="text-center py-8 text-gray-500">
                      <Users className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                      <p>No users found for this practice.</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Users className="h-5 w-5 mr-2" />
                    Practice User Management
                  </CardTitle>
                  <p className="text-sm text-gray-600">Select a practice to view and manage their users (dentists and staff)</p>
                </CardHeader>
                <CardContent>
                  <div className="mb-4">
                    <div className="flex items-center space-x-2">
                      <Search className="h-4 w-4" />
                      <Input
                        placeholder="Search practices..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="max-w-md"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    {practices
                      .filter(practice => {
                        // Text search filter
                        const matchesSearch = practice.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                             practice.email?.toLowerCase().includes(searchTerm.toLowerCase());
                        
                        // Status filter
                        if (statusFilter === 'all') return matchesSearch;
                        
                        const practiceStatus = practice.subscription?.status || 'inactive';
                        return matchesSearch && practiceStatus === statusFilter;
                      })
                      .map((practice) => (
                      <div key={practice.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50">
                        <div>
                          <h3 className="font-semibold">{practice.name}</h3>
                          <p className="text-sm text-gray-600">{practice.email}</p>
                          <div className="flex items-center space-x-2 mt-1">
                            <Badge 
                              variant={practice.subscription?.status === 'active' ? 'default' : 
                                       practice.subscription?.status === 'trial' ? 'secondary' : 'destructive'}
                            >
                              {practice.subscription?.status || 'unknown'}
                            </Badge>
                            <span className="text-xs text-gray-500">
                              {practice.user_count || 0} users
                            </span>
                          </div>
                        </div>
                        <Button 
                          size="sm" 
                          onClick={() => {
                            setSelectedPractice(practice);
                            loadUsers(practice.id);
                          }}
                        >
                          <Users className="h-4 w-4 mr-2" />
                          View Users
                        </Button>
                      </div>
                    ))}
                  </div>
                  
                  {practices.length === 0 && (
                    <div className="text-center py-8 text-gray-500">
                      <Building2 className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                      <p>No practices found. Load practices from the Practices tab first.</p>
                      <Button 
                        className="mt-4" 
                        onClick={() => {
                          loadPractices();
                        }}
                      >
                        Load Practices
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </CustomTabsContent>

          <CustomTabsContent value="analytics" activeTab={activeTab} className="space-y-6">
            <AdminAnalytics />
          </CustomTabsContent>

          <CustomTabsContent value="app-health" activeTab={activeTab} className="space-y-6">
            <AdminAppHealth />
          </CustomTabsContent>

          <CustomTabsContent value="procedures" activeTab={activeTab} className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold">Global Procedure Management</h2>
              <div className="flex space-x-2">
                <Button onClick={loadProcedures}>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Refresh
                </Button>
                <Button onClick={() => setShowAddProcedureForm(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Procedure
                </Button>
              </div>
            </div>

            {/* Add Procedure Form */}
            {showAddProcedureForm && (
              <ErrorBoundary>
                <Card>
                <CardHeader>
                  <CardTitle>Add New Global Procedure</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <form onSubmit={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    console.log('🚨 FORM SUBMIT INTERCEPTED - This should not happen!');
                    alert('Form submitted! This should be prevented.');
                    return false;
                  }} onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      e.stopPropagation();
                      console.log('🚨 ENTER KEY INTERCEPTED');
                      alert('Enter key pressed! This might be causing auto-submission.');
                      return false;
                    }
                  }}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        Procedure Name <span className="text-red-500">*</span>
                      </label>
                      <Input 
                        value={newProcedure.name}
                        onChange={(e) => setNewProcedure(prev => ({...prev, name: e.target.value}))}
                        placeholder="e.g., Root Canal Therapy"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        Specialty <span className="text-red-500">*</span>
                      </label>
                      <select
                        className="w-full p-2 border rounded-md"
                        value={newProcedure.specialty}
                        onChange={(e) => {
                          const specialty = specialties.find(s => s.id === e.target.value);
                          setNewProcedure(prev => ({
                            ...prev, 
                            specialty: e.target.value,
                            specialtyName: specialty?.name || ''
                          }));
                        }}
                      >
                        <option value="">Select Specialty</option>
                        {specialties.map(specialty => (
                          <option key={specialty.id} value={specialty.id}>
                            {specialty.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        Duration
                      </label>
                      <Input 
                        value={newProcedure.duration}
                        onChange={(e) => setNewProcedure(prev => ({...prev, duration: e.target.value}))}
                        placeholder="e.g., 3-7 days recovery (optional)"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Purpose <span className="text-red-500">*</span>
                      <span className="text-xs text-gray-500 ml-2">(HTML formatting accepted)</span>
                    </label>
                    <textarea
                      className="w-full p-2 border rounded-md h-24 font-mono text-sm"
                      value={newProcedure.purpose}
                      onChange={(e) => setNewProcedure(prev => ({...prev, purpose: e.target.value}))}
                      placeholder="e.g., <p>You received amalgam (silver) filling(s) today. These restorations are designed to restore the full function and structure of your teeth.</p>"
                      style={{whiteSpace: 'pre-wrap'}}
                    />
                  </div>

                  {/* First 24 Hours */}
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      First 24 Hours
                      <span className="text-xs text-gray-500 ml-2">(HTML formatting accepted)</span>
                    </label>
                    {newProcedure.first24Hours.map((item, index) => (
                      <div key={index} className="flex items-start space-x-2 mb-3">
                        <textarea
                          className="flex-1 p-2 border rounded-md min-h-[60px] resize-y font-mono text-sm"
                          value={item}
                          onChange={(e) => updateArrayItem('first24Hours', index, e.target.value)}
                          placeholder="e.g., <ul><li>Some sensitivity to temperature and pressure is normal</li><li>Avoid chewing on treated side until numbness wears off</li></ul>"
                          style={{whiteSpace: 'pre-wrap'}}
                        />
                        <Button 
                          type="button" 
                          size="sm" 
                          variant="outline" 
                          onClick={() => removeArrayItem('first24Hours', index)}
                          className="mt-1"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                    <Button type="button" size="sm" variant="outline" onClick={() => addArrayItem('first24Hours')}>
                      <Plus className="h-4 w-4 mr-1" /> Add Instruction
                    </Button>
                  </div>

                  {/* Pain and Swelling */}
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Pain and Swelling
                      <span className="text-xs text-gray-500 ml-2">(HTML formatting accepted)</span>
                    </label>
                    {newProcedure.painAndSwelling.map((item, index) => (
                      <div key={index} className="flex items-start space-x-2 mb-3">
                        <textarea
                          className="flex-1 p-2 border rounded-md min-h-[60px] resize-y font-mono text-sm"
                          value={item}
                          onChange={(e) => updateArrayItem('painAndSwelling', index, e.target.value)}
                          placeholder="e.g., <ul><li>Mild discomfort is normal</li><li>Take over-the-counter pain relievers as directed</li><li>Apply cold compress for 10-15 minutes if swelling occurs</li></ul>"
                          style={{whiteSpace: 'pre-wrap'}}
                        />
                        <Button 
                          type="button" 
                          size="sm" 
                          variant="outline" 
                          onClick={() => removeArrayItem('painAndSwelling', index)}
                          className="mt-1"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                    <Button type="button" size="sm" variant="outline" onClick={() => addArrayItem('painAndSwelling')}>
                      <Plus className="h-4 w-4 mr-1" /> Add Instruction
                    </Button>
                  </div>

                  {/* Oral Hygiene */}
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Oral Hygiene
                      <span className="text-xs text-gray-500 ml-2">(HTML formatting accepted)</span>
                    </label>
                    {newProcedure.oralHygiene.map((item, index) => (
                      <div key={index} className="flex items-start space-x-2 mb-3">
                        <textarea
                          className="flex-1 p-2 border rounded-md min-h-[60px] resize-y font-mono text-sm"
                          value={item}
                          onChange={(e) => updateArrayItem('oralHygiene', index, e.target.value)}
                          placeholder="e.g., <ul><li>Resume normal brushing and flossing after 24 hours</li><li>Use a soft-bristled toothbrush</li><li>Be gentle around the treated area initially</li></ul>"
                          style={{whiteSpace: 'pre-wrap'}}
                        />
                        <Button 
                          type="button" 
                          size="sm" 
                          variant="outline" 
                          onClick={() => removeArrayItem('oralHygiene', index)}
                          className="mt-1"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                    <Button type="button" size="sm" variant="outline" onClick={() => addArrayItem('oralHygiene')}>
                      <Plus className="h-4 w-4 mr-1" /> Add Instruction
                    </Button>
                  </div>

                  {/* Diet */}
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Diet
                      <span className="text-xs text-gray-500 ml-2">(HTML formatting accepted)</span>
                    </label>
                    {newProcedure.diet.map((item, index) => (
                      <div key={index} className="flex items-start space-x-2 mb-3">
                        <textarea
                          className="flex-1 p-2 border rounded-md min-h-[60px] resize-y font-mono text-sm"
                          value={item}
                          onChange={(e) => updateArrayItem('diet', index, e.target.value)}
                          placeholder="e.g., <ul><li>Avoid hard, sticky, or chewy foods for the first few days</li><li>Choose soft foods like yogurt, pasta, soup</li><li>Avoid extremely hot or cold foods initially</li></ul>"
                          style={{whiteSpace: 'pre-wrap'}}
                        />
                        <Button 
                          type="button" 
                          size="sm" 
                          variant="outline" 
                          onClick={() => removeArrayItem('diet', index)}
                          className="mt-1"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                    <Button type="button" size="sm" variant="outline" onClick={() => addArrayItem('diet')}>
                      <Plus className="h-4 w-4 mr-1" /> Add Instruction
                    </Button>
                  </div>

                  {/* Activity */}
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Activity
                      <span className="text-xs text-gray-500 ml-2">(HTML formatting accepted)</span>
                    </label>
                    {newProcedure.activity.map((item, index) => (
                      <div key={index} className="flex items-start space-x-2 mb-3">
                        <textarea
                          className="flex-1 p-2 border rounded-md min-h-[60px] resize-y font-mono text-sm"
                          value={item}
                          onChange={(e) => updateArrayItem('activity', index, e.target.value)}
                          placeholder="e.g., <ul><li>Normal activities can be resumed immediately</li><li>Avoid strenuous exercise for first 24 hours</li><li>No restrictions on work or daily routines</li></ul>"
                          style={{whiteSpace: 'pre-wrap'}}
                        />
                        <Button 
                          type="button" 
                          size="sm" 
                          variant="outline" 
                          onClick={() => removeArrayItem('activity', index)}
                          className="mt-1"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                    <Button type="button" size="sm" variant="outline" onClick={() => addArrayItem('activity')}>
                      <Plus className="h-4 w-4 mr-1" /> Add Instruction
                    </Button>
                  </div>

                  {/* Special Precautions */}
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Special Precautions
                      <span className="text-xs text-gray-500 ml-2">(HTML formatting accepted)</span>
                    </label>
                    {newProcedure.specialPrecautions.map((item, index) => (
                      <div key={index} className="flex items-start space-x-2 mb-3">
                        <textarea
                          className="flex-1 p-2 border rounded-md min-h-[60px] resize-y font-mono text-sm"
                          value={item}
                          onChange={(e) => updateArrayItem('specialPrecautions', index, e.target.value)}
                          placeholder="e.g., <ul><li>Avoid chewing gum or sticky candies</li><li>Do not use the tooth as a tool for opening packages</li><li>Protect filling from excessive grinding or clenching</li></ul>"
                          style={{whiteSpace: 'pre-wrap'}}
                        />
                        <Button 
                          type="button" 
                          size="sm" 
                          variant="outline" 
                          onClick={() => removeArrayItem('specialPrecautions', index)}
                          className="mt-1"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                    <Button type="button" size="sm" variant="outline" onClick={() => addArrayItem('specialPrecautions')}>
                      <Plus className="h-4 w-4 mr-1" /> Add Precaution
                    </Button>
                  </div>

                  {/* Follow-up */}
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Follow-up
                      <span className="text-xs text-gray-500 ml-2">(HTML formatting accepted)</span>
                    </label>
                    {newProcedure.followUp.map((item, index) => (
                      <div key={index} className="flex items-start space-x-2 mb-3">
                        <textarea
                          className="flex-1 p-2 border rounded-md min-h-[60px] resize-y font-mono text-sm"
                          value={item}
                          onChange={(e) => updateArrayItem('followUp', index, e.target.value)}
                          placeholder="e.g., <ul><li>Schedule your next cleaning and check-up in 6 months</li><li>Call if you experience persistent pain or sensitivity</li><li>Return for bite adjustment if needed</li></ul>"
                          style={{whiteSpace: 'pre-wrap'}}
                        />
                        <Button 
                          type="button" 
                          size="sm" 
                          variant="outline" 
                          onClick={() => removeArrayItem('followUp', index)}
                          className="mt-1"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                    <Button type="button" size="sm" variant="outline" onClick={() => addArrayItem('followUp')}>
                      <Plus className="h-4 w-4 mr-1" /> Add Follow-up
                    </Button>
                  </div>

                  <div className="flex justify-end space-x-2">
                    <Button 
                      type="button" 
                      variant="outline" 
                      onClick={() => setShowAddProcedureForm(false)}
                    >
                      Cancel
                    </Button>
                    <Button 
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        console.log('🚀 SAVE PROCEDURE BUTTON CLICKED!');
                        handleCreateProcedure();
                      }} 
                      disabled={loading}
                      type="button"
                    >
                      {loading ? <LoadingSpinner className="h-4 w-4 mr-2" /> : null}
                      Save
                    </Button>
                  </div>
                  </form>
                </CardContent>
              </Card>
              </ErrorBoundary>
            )}

            {/* Organized Procedures List by Specialty with Color Coding */}
            <div className="grid grid-cols-1 gap-6">
              {specialties
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((specialty) => {
                  const specialtyProcedures = procedures
                    .filter(proc => proc.specialty === specialty.id)
                    .sort((a, b) => a.name.localeCompare(b.name));
                  
                  if (specialtyProcedures.length === 0) return null;
                  
                  // Color scheme for each specialty
                  const getSpecialtyColors = (specialtyId) => {
                    const colorMap = {
                      'endodontics': {
                        card: 'bg-blue-50 border-blue-200',
                        header: 'bg-blue-100 border-blue-300',
                        icon: 'text-blue-600',
                        badge: 'bg-blue-600 text-white',
                        procedures: 'border-blue-200 hover:bg-blue-25'
                      },
                      'oral-surgery': {
                        card: 'bg-red-50 border-red-200',
                        header: 'bg-red-100 border-red-300',
                        icon: 'text-red-600',
                        badge: 'bg-red-600 text-white',
                        procedures: 'border-red-200 hover:bg-red-25'
                      },
                      'prosthodontics': {
                        card: 'bg-purple-50 border-purple-200',
                        header: 'bg-purple-100 border-purple-300',
                        icon: 'text-purple-600',
                        badge: 'bg-purple-600 text-white',
                        procedures: 'border-purple-200 hover:bg-purple-25'
                      },
                      'periodontics': {
                        card: 'bg-green-50 border-green-200',
                        header: 'bg-green-100 border-green-300',
                        icon: 'text-green-600',
                        badge: 'bg-green-600 text-white',
                        procedures: 'border-green-200 hover:bg-green-25'
                      },
                      'general-dentistry': {
                        card: 'bg-orange-50 border-orange-200',
                        header: 'bg-orange-100 border-orange-300',
                        icon: 'text-orange-600',
                        badge: 'bg-orange-600 text-white',
                        procedures: 'border-orange-200 hover:bg-orange-25'
                      },
                      'orthodontics': {
                        card: 'bg-teal-50 border-teal-200',
                        header: 'bg-teal-100 border-teal-300',
                        icon: 'text-teal-600',
                        badge: 'bg-teal-600 text-white',
                        procedures: 'border-teal-200 hover:bg-teal-25'
                      },
                      'oral-medicine': {
                        card: 'bg-indigo-50 border-indigo-200',
                        header: 'bg-indigo-100 border-indigo-300',
                        icon: 'text-indigo-600',
                        badge: 'bg-indigo-600 text-white',
                        procedures: 'border-indigo-200 hover:bg-indigo-25'
                      }
                    };
                    return colorMap[specialtyId] || colorMap['general-dentistry'];
                  };
                  
                  const colors = getSpecialtyColors(specialty.id);
                  
                  return (
                    <Card key={specialty.id} className={`${colors.card} border-2`}>
                      <CardHeader className={`${colors.header} rounded-t-lg border-b-2`}>
                        <CardTitle className="flex items-center justify-between">
                          <div className="flex items-center">
                            <Stethoscope className={`h-5 w-5 mr-2 ${colors.icon}`} />
                            <span className={`font-bold ${colors.icon}`}>{specialty.name}</span>
                            <Badge className={`ml-2 ${colors.badge}`}>
                              {specialtyProcedures.length} procedures
                            </Badge>
                          </div>
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="pt-4">
                        <div className="space-y-3">
                          {specialtyProcedures.map((procedure) => (
                            <div key={procedure.id} className={`border-2 rounded-lg p-4 ${colors.procedures} transition-colors duration-200`}>
                              <div className="flex items-center justify-between">
                                <div className="flex-1">
                                  <h3 className={`font-semibold text-lg ${colors.icon}`}>{procedure.name}</h3>
                                  <div className="flex items-center space-x-4 text-sm text-gray-600 mt-1">
                                    <span className="font-medium">{procedure.duration}</span>
                                    <span className="text-xs text-gray-500">
                                      ID: {procedure.id}
                                    </span>
                                  </div>
                                  <p className="text-sm text-gray-700 mt-2 line-clamp-2">
                                    {procedure.purpose ? procedure.purpose.replace(/<[^>]*>/g, '') : 'No description available'}
                                  </p>
                                </div>
                                <div className="flex flex-col space-y-2 ml-4">
                                  <Button 
                                    size="sm" 
                                    variant="outline"
                                    className={`border-2 ${colors.icon.replace('text-', 'border-')} hover:${colors.card}`}
                                    onClick={() => setEditingProcedure(procedure.id === editingProcedure ? null : procedure.id)}
                                  >
                                    <Edit className="h-4 w-4 mr-1" />
                                    {editingProcedure === procedure.id ? 'Cancel' : 'Edit'}
                                  </Button>
                                  <Button 
                                    size="sm" 
                                    variant="destructive"
                                    onClick={() => deleteProcedure(procedure.id)}
                                  >
                                    <Trash2 className="h-4 w-4 mr-1" />
                                    Delete
                                  </Button>
                                </div>
                              </div>

                              {editingProcedure === procedure.id && (
                                <div className={`mt-4 pt-4 border-t-2 ${colors.icon.replace('text-', 'border-')} space-y-4 bg-white rounded-lg p-4 shadow-sm`}>
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                      <label className="block text-sm font-medium mb-1">Procedure Name</label>
                                      <Input
                                        defaultValue={procedure.name}
                                        className={`border-2 ${colors.icon.replace('text-', 'border-')} focus:${colors.card}`}
                                        onBlur={(e) => updateProcedure(procedure.id, { name: e.target.value })}
                                      />
                                    </div>
                                    <div>
                                      <label className="block text-sm font-medium mb-1">Duration</label>
                                      <Input
                                        defaultValue={procedure.duration}
                                        className={`border-2 ${colors.icon.replace('text-', 'border-')} focus:${colors.card}`}
                                        onBlur={(e) => updateProcedure(procedure.id, { duration: e.target.value })}
                                      />
                                    </div>
                                  </div>
                                  <div>
                                    <label className="block text-sm font-medium mb-1">Purpose</label>
                                    <textarea
                                      className={`w-full p-2 border-2 rounded-md h-24 ${colors.icon.replace('text-', 'border-')} focus:${colors.card}`}
                                      defaultValue={procedure.purpose || procedure.overview || ''}
                                      onBlur={(e) => updateProcedure(procedure.id, { purpose: e.target.value })}
                                    />
                                  </div>
                                  
                                  {/* Show all sections for new format procedures */}
                                  {procedure.purpose && (
                                    <>
                                      <div>
                                        <label className="block text-sm font-medium mb-1">First 24 Hours</label>
                                        <textarea
                                          className={`w-full p-2 border-2 rounded-md h-20 ${colors.icon.replace('text-', 'border-')} focus:${colors.card}`}
                                          defaultValue={Array.isArray(procedure.first24Hours) ? procedure.first24Hours.join('\n') : ''}
                                          onBlur={(e) => updateProcedure(procedure.id, { first24Hours: e.target.value.split('\n').filter(line => line.trim()) })}
                                        />
                                      </div>
                                      
                                      <div>
                                        <label className="block text-sm font-medium mb-1">Pain and Swelling</label>
                                        <textarea
                                          className={`w-full p-2 border-2 rounded-md h-20 ${colors.icon.replace('text-', 'border-')} focus:${colors.card}`}
                                          defaultValue={Array.isArray(procedure.painAndSwelling) ? procedure.painAndSwelling.join('\n') : ''}
                                          onBlur={(e) => updateProcedure(procedure.id, { painAndSwelling: e.target.value.split('\n').filter(line => line.trim()) })}
                                        />
                                      </div>
                                      
                                      <div>
                                        <label className="block text-sm font-medium mb-1">Oral Hygiene</label>
                                        <textarea
                                          className={`w-full p-2 border-2 rounded-md h-20 ${colors.icon.replace('text-', 'border-')} focus:${colors.card}`}
                                          defaultValue={Array.isArray(procedure.oralHygiene) ? procedure.oralHygiene.join('\n') : ''}
                                          onBlur={(e) => updateProcedure(procedure.id, { oralHygiene: e.target.value.split('\n').filter(line => line.trim()) })}
                                        />
                                      </div>
                                      
                                      <div>
                                        <label className="block text-sm font-medium mb-1">Diet</label>
                                        <textarea
                                          className={`w-full p-2 border-2 rounded-md h-20 ${colors.icon.replace('text-', 'border-')} focus:${colors.card}`}
                                          defaultValue={Array.isArray(procedure.diet) ? procedure.diet.join('\n') : ''}
                                          onBlur={(e) => updateProcedure(procedure.id, { diet: e.target.value.split('\n').filter(line => line.trim()) })}
                                        />
                                      </div>
                                    </>
                                  )}
                                  <div>
                                    <label className="block text-sm font-medium mb-1">Specialty</label>
                                    <select
                                      className={`w-full p-2 border-2 rounded-md ${colors.icon.replace('text-', 'border-')} focus:${colors.card}`}
                                      defaultValue={procedure.specialty}
                                      onChange={(e) => {
                                        const newSpecialty = specialties.find(s => s.id === e.target.value);
                                        updateProcedure(procedure.id, { 
                                          specialty: e.target.value,
                                          specialtyName: newSpecialty?.name || ''
                                        });
                                      }}
                                    >
                                      {specialties.map(spec => (
                                        <option key={spec.id} value={spec.id}>
                                          {spec.name}
                                        </option>
                                      ))}
                                    </select>
                                  </div>
                                  
                                  {/* Save Button */}
                                  <div className="flex justify-end space-x-2 pt-4 border-t">
                                    <Button 
                                      variant="outline"
                                      onClick={() => setEditingProcedure(null)}
                                    >
                                      Cancel
                                    </Button>
                                    <Button 
                                      onClick={() => {
                                        setEditingProcedure(null);
                                        alert('Changes saved successfully!');
                                      }}
                                      className="bg-blue-600 hover:bg-blue-700 text-white"
                                    >
                                      <Check className="h-4 w-4 mr-1" />
                                      Save Changes
                                    </Button>
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
            </div>

            {/* Summary Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <BookOpen className="h-5 w-5 mr-2" />
                  Procedure Summary
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-blue-600">{procedures.length}</div>
                    <p className="text-sm text-gray-600">Total Procedures</p>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-600">{specialties.length}</div>
                    <p className="text-sm text-gray-600">Specialties</p>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-purple-600">
                      {Math.round(procedures.length / specialties.length) || 0}
                    </div>
                    <p className="text-sm text-gray-600">Avg per Specialty</p>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-orange-600">
                      {specialties.length > 0 ? Math.max(...specialties.map(s => 
                        procedures.filter(p => p.specialty === s.id).length
                      )) : 0}
                    </div>
                    <p className="text-sm text-gray-600">Largest Specialty</p>
                  </div>
                </div>

                {procedures.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    <BookOpen className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                    <p>No procedures found. Add your first global procedure.</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Deployment Controls */}
            {procedures.length > 0 && (
              <>
                <Card>
                  <CardHeader>
                    <CardTitle>Deploy Procedures to Practices</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <p className="text-sm text-gray-600">
                      Procedures are automatically available to all practices. Use these controls for manual deployments.
                    </p>
                    <div className="flex space-x-2">
                      <Button 
                        onClick={() => deployProcedures(procedures.map(p => p.id), 'all_practices')}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        Deploy to ALL Users
                      </Button>
                      <Button 
                        onClick={() => setShowSelectPracticeModal(true)}
                        variant="outline"
                        className="bg-blue-50 hover:bg-blue-100 border-blue-300"
                      >
                        Deploy to Specific User
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                {/* Select Practice Modal */}
                {showSelectPracticeModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                  <div className="bg-white rounded-lg p-6 w-96 max-w-full mx-4">
                    <h3 className="text-lg font-semibold mb-4">Deploy to Specific User</h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium mb-2">Search and Select Practice:</label>
                        <Input
                          placeholder="Search practices by name or email..."
                          value={practiceSearchTerm}
                          onChange={(e) => setPracticeSearchTerm(e.target.value)}
                          className="mb-2"
                        />
                        <select 
                          className="w-full p-2 border rounded-md max-h-40 overflow-y-auto"
                          value={selectedPracticeForDeployment}
                          onChange={(e) => setSelectedPracticeForDeployment(e.target.value)}
                          size="6"
                        >
                          <option value="">Choose a practice...</option>
                          {practices
                            .filter(practice => 
                              !practiceSearchTerm || 
                              practice.practiceName.toLowerCase().includes(practiceSearchTerm.toLowerCase()) ||
                              practice.adminEmail.toLowerCase().includes(practiceSearchTerm.toLowerCase())
                            )
                            .map(practice => (
                              <option key={practice.id} value={practice.id}>
                                {practice.practiceName} ({practice.adminEmail})
                              </option>
                            ))}
                        </select>
                      </div>
                      <div className="flex justify-end space-x-2">
                        <Button 
                          variant="outline" 
                          onClick={() => {
                            setShowSelectPracticeModal(false);
                            setSelectedPracticeForDeployment('');
                            setPracticeSearchTerm('');
                          }}
                        >
                          Cancel
                        </Button>
                        <Button 
                          onClick={() => {
                            if (selectedPracticeForDeployment) {
                              deployProcedures(procedures.map(p => p.id), 'specific_practice', selectedPracticeForDeployment);
                              setShowSelectPracticeModal(false);
                              setSelectedPracticeForDeployment('');
                              setPracticeSearchTerm('');
                            } else {
                              alert('Please select a practice first');
                            }
                          }}
                          disabled={!selectedPracticeForDeployment}
                          className="bg-blue-600 hover:bg-blue-700 text-white"
                        >
                          Deploy to Selected User
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              </>
            )}
          </CustomTabsContent>

          <CustomTabsContent value="registrations" activeTab={activeTab} className="space-y-6">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="flex items-center space-x-2">
                  <UserX className="h-5 w-5 text-blue-600" />
                  <span>New Registrations</span>
                </CardTitle>
                <Button onClick={loadRegistrations} variant="outline" size="sm">
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Refresh
                </Button>
              </CardHeader>
              <CardContent>
                {registrationsLoading ? (
                  <div className="flex justify-center py-8">
                    <LoadingSpinner />
                  </div>
                ) : registrations.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <UserX className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                    <p className="text-lg font-medium">No registration attempts found</p>
                    <p className="text-sm">Registration attempts will appear here</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {registrations.map((registration, index) => (
                      <div key={index} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-2">
                              <h3 className="font-semibold text-lg">{registration.practiceName}</h3>
                              <Badge 
                                variant={registration.status === 'success' ? 'default' : 
                                        registration.status === 'blocked' ? 'destructive' : 'secondary'}
                                className={
                                  registration.status === 'success' ? 'bg-green-100 text-green-800 border-green-200' :
                                  registration.status === 'blocked' ? 'bg-red-100 text-red-800 border-red-200' :
                                  'bg-yellow-100 text-yellow-800 border-yellow-200'
                                }
                              >
                                {registration.status === 'success' ? '✅ PAID' : 
                                 registration.status === 'blocked' ? '❌ BLOCKED' : 
                                 registration.status === 'trial_registered' ? '⚠️ TRIAL' : registration.status}
                              </Badge>
                              {registration.registration_type === 'samcart' && (
                                <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                                  SamCart
                                </Badge>
                              )}
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                              <div>
                                <span className="font-medium text-gray-600">Email:</span> {registration.email}
                              </div>
                              <div>
                                <span className="font-medium text-gray-600">Date:</span> {
                                  new Date(registration.attempted_at).toLocaleString()
                                }
                              </div>
                              <div>
                                <span className="font-medium text-gray-600">Payment Verified:</span> {
                                  registration.payment_verified ? (
                                    <span className="text-green-600 font-medium">✅ Yes</span>
                                  ) : (
                                    <span className="text-red-600 font-medium">❌ No</span>
                                  )
                                }
                              </div>
                              {registration.registration_type && (
                                <div>
                                  <span className="font-medium text-gray-600">Type:</span> {
                                    registration.registration_type === 'samcart' ? 'SamCart Payment' : 'Trial Registration'
                                  }
                                </div>
                              )}
                              {registration.reason && (
                                <div className="md:col-span-2">
                                  <span className="font-medium text-gray-600">Reason:</span> 
                                  <span className="text-red-600 ml-1">{registration.reason}</span>
                                </div>
                              )}
                            </div>
                            
                            {registration.practice_id && (
                              <div className="mt-2 text-xs text-gray-500">
                                Practice ID: {registration.practice_id}
                              </div>
                            )}
                          </div>
                          
                          <div className="ml-4 flex items-center space-x-2">
                            {registration.status === 'success' ? (
                              <CheckCircle className="h-6 w-6 text-green-500" />
                            ) : registration.status === 'blocked' ? (
                              <XCircle className="h-6 w-6 text-red-500" />
                            ) : (
                              <AlertTriangle className="h-6 w-6 text-yellow-500" />
                            )}
                            <button
                              onClick={() => deleteRegistrationAttempt(registration.id, registration.email)}
                              className="p-2 hover:bg-red-50 rounded-full transition-colors group"
                              title="Delete this registration attempt"
                            >
                              <Trash2 className="h-5 w-5 text-gray-400 group-hover:text-red-600" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </CustomTabsContent>

          <CustomTabsContent value="support" activeTab={activeTab} className="space-y-6">
            <AdminSupport />
          </CustomTabsContent>

          <CustomTabsContent value="compliance" activeTab={activeTab} className="space-y-6">
            <ComplianceAcknowledgmentsAdmin adminToken={adminToken} />
          </CustomTabsContent>

          <CustomTabsContent value="baa" activeTab={activeTab} className="space-y-6">
            <BaaAdminPanel adminToken={adminToken} />
          </CustomTabsContent>

          <CustomTabsContent value="beta-program" activeTab={activeTab} className="space-y-6">
            <BetaProgramAdmin adminToken={adminToken} />
          </CustomTabsContent>

          <CustomTabsContent value="offices-plans" activeTab={activeTab} className="space-y-6">
            <OfficesAdmin adminToken={adminToken} />
          </CustomTabsContent>

          <CustomTabsContent value="enterprise-inquiries" activeTab={activeTab} className="space-y-6">
            <EnterpriseInquiriesAdmin adminToken={adminToken} />
          </CustomTabsContent>

          <CustomTabsContent value="welcome-video" activeTab={activeTab} className="space-y-6">
            <WelcomeVideoStatsAdmin adminToken={adminToken} />
          </CustomTabsContent>

          <CustomTabsContent value="sms-diagnostics" activeTab={activeTab} className="space-y-6">
            <AdminSmsLogsPanel adminToken={adminToken} />
          </CustomTabsContent>

          <CustomTabsContent value="system" activeTab={activeTab} className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>System Management</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <Button onClick={loadDashboardData} className="w-full">
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Refresh All Data
                </Button>
                <Button variant="outline" className="w-full">
                  <Download className="h-4 w-4 mr-2" />
                  Download System Report
                </Button>
                <Button variant="outline" className="w-full">
                  <FileText className="h-4 w-4 mr-2" />
                  View System Logs
                </Button>
              </CardContent>
            </Card>

            <HealthCheckCard adminToken={adminToken} />

            <SanitizeContentCard adminToken={adminToken} />

            <AppSettingsCard adminToken={adminToken} />

            <StandaloneMigrationCard adminToken={adminToken} />

            <ABTestCard
              adminToken={adminToken}
              endpoint="day11"
              testid="day11-ab-card"
              accentColor="purple"
              title="Day 11 Email — Subject Line A/B Test"
              description="Day 11 conversion email randomly assigns a subject line (50/50 split). Conversion = practice subscribed (status = active) after Day 11 email was sent."
            />

            <ABTestCard
              adminToken={adminToken}
              endpoint="expired"
              testid="expired-ab-card"
              accentColor="amber"
              title="Day 15 Trial Expired Email — Subject Line A/B Test"
              description="The single most-read email in the trial sequence. Randomly assigns Action-oriented vs. Loss-framed subject (50/50 split)."
            />

            <EmailAuditCard adminToken={adminToken} />

            <BroadcastEmailCard adminToken={adminToken} />

            <CheckInCampaignCard adminToken={adminToken} />
          </CustomTabsContent>

          <CustomTabsContent value="dentalstaff" activeTab={activeTab} className="space-y-6">
            <DentalStaffAdminPanel adminToken={adminToken} />
          </CustomTabsContent>

          <CustomTabsContent value="reinterview" activeTab={activeTab} className="space-y-6">
            <ReinterviewPanel adminToken={adminToken} />
          </CustomTabsContent>

          <CustomTabsContent value="testimonials" activeTab={activeTab} className="space-y-6">
            <TestimonialsAdminPanel adminToken={adminToken} />
          </CustomTabsContent>

          <CustomTabsContent value="leadmagnet" activeTab={activeTab} className="space-y-6">
            <LeadMagnetAdminPanel adminToken={adminToken} />
          </CustomTabsContent>

          <CustomTabsContent value="ecard-requests" activeTab={activeTab} className="space-y-6">
            <EcardRequestsAdminPanel adminToken={adminToken} />
          </CustomTabsContent>

          <CustomTabsContent value="billing" activeTab={activeTab} className="space-y-6">
            <AdminBillingPanel adminToken={adminToken} />
          </CustomTabsContent>

          <CustomTabsContent value="announcements" activeTab={activeTab} className="space-y-6">
            <AdminAnnouncementsPanel adminToken={adminToken} />
          </CustomTabsContent>
        </CustomTabs>

        {/* Tutorial Management Modal */}
        <TutorialManagement
          isOpen={showTutorialManagement}
          onClose={() => setShowTutorialManagement(false)}
          adminToken={adminToken}
        />

        {/* Beta Tester Management Modal */}
        <BetaTesterManagement
          isOpen={showBetaTesterManagement}
          onClose={() => setShowBetaTesterManagement(false)}
          adminToken={adminToken}
        />

        {/* Disclaimer Acceptances Modal */}
        <DisclaimerAcceptancesView
          isOpen={showDisclaimerAcceptances}
          onClose={() => setShowDisclaimerAcceptances(false)}
          adminToken={adminToken}
        />

        {/* Med Card Disclaimer Acceptances Modal */}
        <MedCardDisclaimerView
          isOpen={showMedCardDisclaimers}
          onClose={() => setShowMedCardDisclaimers(false)}
          adminToken={adminToken}
        />

        {/* Email Activity Monitor Modal */}
        <EmailActivityMonitor
          isOpen={showEmailActivityMonitor}
          onClose={() => setShowEmailActivityMonitor(false)}
          adminToken={adminToken}
        />
    </AdminShell>
  );
};

const AdminLogin = () => {
  const [credentials, setCredentials] = useState({ email: 'cganz2279@gmail.com', password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [networkError, setNetworkError] = useState(false);
  const [adminToken, setAdminToken] = useState(() => {
    const storedToken = localStorage.getItem('adminToken');
    console.log('🔑 Stored admin token found:', storedToken ? 'Yes' : 'No');
    return storedToken;
  });

  console.log('VERSION 2.0 LOADED - Using dental-admin-3 backend');
  const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api/admin`;
  
  // Validate token on component mount using XMLHttpRequest to avoid extension interference
  useEffect(() => {
    if (adminToken) {
      console.log('🔍 Validating stored admin token...');
      
      const xhr = new XMLHttpRequest();
      xhr.open('GET', `${API_BASE}/dashboard`, true);
      xhr.setRequestHeader('Authorization', `Bearer ${adminToken || localStorage.getItem('adminToken')}`);
      
      xhr.onreadystatechange = function() {
        if (xhr.readyState === 4) {
          if (xhr.status === 200) {
            console.log('✅ Stored admin token is valid');
          } else if (xhr.status === 401) {
            console.warn('🚨 Stored admin token is invalid, clearing...');
            localStorage.removeItem('adminToken');
            setAdminToken(null);
          }
          // Don't clear token on other errors (network issues, etc.)
        }
      };
      
      xhr.onerror = function() {
        console.warn('⚠️ Token validation request failed, keeping token');
        // Don't clear the token on network errors
      };
      
      xhr.send();
    }
  }, [API_BASE]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    console.log('🔐 Admin login attempt:', { credentials, API_BASE });

    // Use XMLHttpRequest to avoid extension interference with fetch API
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${API_BASE}/login`, true);
    xhr.setRequestHeader('Content-Type', 'application/json');
    
    xhr.onreadystatechange = function() {
      if (xhr.readyState === 4) {
        setLoading(false);
        
        if (xhr.status === 200) {
          try {
            const data = JSON.parse(xhr.responseText);
            console.log('🔐 Login response data:', data);
            
            if (data.success && data.token) {
              console.log('✅ Admin login successful, storing token');
              localStorage.setItem('adminToken', data.token);
              setAdminToken(data.token);
            } else {
              console.error('❌ Invalid login response:', data);
              setError('Invalid login response');
            }
          } catch (parseError) {
            console.error('❌ Failed to parse response:', parseError);
            setError('Invalid server response');
          }
        } else {
          console.error('❌ Login failed:', xhr.status, xhr.responseText);
          setError('Invalid admin credentials');
        }
      }
    };
    
    xhr.onerror = function() {
      setLoading(false);
      setNetworkError(true);
      console.error('❌ Network error during login - possible extension interference');
      setError('Network error during login. This may be caused by browser extensions blocking the request. Try: 1) Disable ad blockers, 2) Use Incognito mode, or 3) Try a different browser.');
    };
    
    xhr.ontimeout = function() {
      setLoading(false);
      setNetworkError(true);
      console.error('❌ Login request timed out');
      setError('Login request timed out. Please check your internet connection or try disabling browser extensions.');
    };
    
    xhr.timeout = 15000; // 15 second timeout
    xhr.send(JSON.stringify(credentials));
  };

  // If logged in, show admin dashboard
  if (adminToken) {
    return <AdminDashboard adminToken={adminToken} setAdminToken={setAdminToken} />;
  }

  // Show login form
  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-red-50 flex items-center justify-center">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="text-2xl font-bold text-center flex flex-col items-center justify-center gap-3">
            <img src="/practcom-logo.png" alt="PractCom" className="h-12 w-auto" />
            Admin Login
            <p className="text-xs text-gray-400 font-normal tracking-wide mt-0">
              The Total Practice Communication System for Dental Offices
            </p>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* Network Error Warning Banner */}
          {networkError && (
            <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-3 py-2 rounded text-sm mb-4">
              <strong>⚠️ Connection Issue Detected</strong>
              <p className="mt-1">Browser extensions may be blocking login requests. Try:</p>
              <ul className="list-disc list-inside mt-1 text-xs">
                <li>Disable ad blockers or privacy extensions</li>
                <li>Use Incognito/Private browsing mode</li>
                <li>Try a different browser (Chrome, Firefox, Edge)</li>
              </ul>
            </div>
          )}
          
          <form onSubmit={handleLogin} className="space-y-4" autoComplete="off" data-lpignore="true" data-form-type="other">
            {error && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-3 py-2 rounded text-sm">
                {typeof error === 'string' ? error : JSON.stringify(error)}
              </div>
            )}
            
            <div>
              <Input
                type="text"
                inputMode="email"
                placeholder="Admin Email"
                value={credentials.email}
                onChange={(e) => setCredentials(prev => ({ ...prev, email: e.target.value }))}
                required
                autoComplete="off"
                data-lpignore="true"
                data-form-type="other"
                name="admin-email-field"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-2">Admin Password</label>
              <div className="relative">
                <Input
                  type={showPassword ? "text" : "password"}
                  placeholder="Admin Password"
                  value={credentials.password}
                  onChange={(e) => setCredentials(prev => ({ ...prev, password: e.target.value }))}
                  required
                  className="pr-10"
                  autoComplete="new-password"
                  data-lpignore="true"
                  data-form-type="other"
                  name="admin-pass-field"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-700"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            
            <Button 
              type="submit" 
              className="w-full bg-red-600 hover:bg-red-700"
              disabled={loading}
            >
              {loading ? <LoadingSpinner className="h-4 w-4 mr-2" /> : null}
              {loading ? 'Logging in...' : 'Login as Admin'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminLogin;